/**
 * MoneyInput — edits an amount in minor units (fils). Shows the currency as a
 * prefix, accepts "8,250.50", and reports `null` while the field is empty or
 * invalid so forms can validate.
 */

import * as React from 'react';
import { minorToInputString, toMinor } from '@nord-living/shared';

import { cn } from '@/lib/utils';

export interface MoneyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: number | null;
  onValueChange: (minor: number | null) => void;
  currency?: string;
  invalid?: boolean;
  allowNegative?: boolean;
}

const AMOUNT = /^-?\d{0,12}([.,]\d{0,2})?$/;

export function parseMoneyDraft(draft: string, allowNegative = false): number | null {
  const cleaned = draft.replace(/[\s,](?=\d{3})/g, '').replace(',', '.');
  if (!/\d/.test(cleaned) || !AMOUNT.test(cleaned)) return null;
  const minor = toMinor(cleaned);
  return !allowNegative && minor < 0 ? null : minor;
}

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  ({ value, onValueChange, currency = 'AED', invalid, allowNegative = false, className, ...props }, ref) => {
    const [draft, setDraft] = React.useState(value === null ? '' : minorToInputString(value));

    React.useEffect(() => {
      if (parseMoneyDraft(draft, allowNegative) !== value) {
        setDraft(value === null ? '' : minorToInputString(value));
      }
      // Only re-sync when the external value changes.
       
    }, [value]);

    return (
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-muted-foreground">
          {currency}
        </span>
        <input
          ref={ref}
          inputMode="decimal"
          autoComplete="off"
          aria-invalid={invalid || undefined}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            onValueChange(parseMoneyDraft(e.target.value, allowNegative));
          }}
          onBlur={() => value !== null && setDraft(minorToInputString(value))}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background py-2 pl-12 pr-3 text-right text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50',
            invalid && 'border-destructive',
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);
MoneyInput.displayName = 'MoneyInput';
